import * as React from 'react';
import { ProtectedPageWrapper } from '../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { useLocation } from 'react-router-dom';
import { FacilityType, InspectionPointUsage, permissionGroupHelper } from '../amorg-common';
import { UserToken } from '../amorg-common';
import { SingleColumnLayout } from '../designSystem/layouts/SingleColumnLayout';
import { InspectionPointConfiguration } from '../features/safetyInspection/InspectionPointConfiguration';
import { getAuthorizedActiveUserOrRedirect } from '../api/serverApiHelpers';
import { getInspectionPointUsage } from '../api/safetyInspectionClientApi';
import { getFacilityTypes } from '../api/locationClientApi';
import { PageHeader } from '../designSystem/PageHeader';
import { HelpModalToolBarButton } from '../designSystem/compositeComponents/HelpModalToolbBarButton';
import { P } from '../designSystem/typography/Body';

type State = {
    facilityTypes:FacilityType[],
    inspectionPointUsage:InspectionPointUsage[]
}
export default function InspectionPointConfigurationPage(): JSX.Element {

    const [state, setState] = React.useState<State>({
        facilityTypes:[],
        inspectionPointUsage:[]
    });
    const [activeUser, setActiveUser] = React.useState<UserToken>(null);

    React.useEffect(() => {
        handleUpdate();
    }, []);

    async function handleUpdate()
    {
        const inspectionPointUsage = (await getInspectionPointUsage())
            .sort((a,b) => a.title.localeCompare(b.title));

        const facilityTypes = await getFacilityTypes();

        setState({
            facilityTypes,
            inspectionPointUsage
        });
    }

    const urlQuerySegment = useLocation().search;
    const urlParams = new URLSearchParams(urlQuerySegment);
    const safetyInspectionId = urlParams.get('safetyInspectionId');

    React.useEffect(function () {
        const getData = async () => {
            const activeUser = await getAuthorizedActiveUserOrRedirect([]);
            setActiveUser(activeUser);
        };
        getData();
    }, []);

    const isMember = activeUser && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member');


    return (
        <ProtectedPageWrapper requiredPermissions={['ViewAssignedActionPlans']} hideMenu={!isMember}>

            <SingleColumnLayout
                noDivider
                contentContainer={
                    <>
                        <PageHeader 
                            headerText="Kontrolpunkter" 
                            headerStyle="secondary-gray"
                            buttons={
                                <HelpModalToolBarButton title='Hjælp til kontrolpunkter' helpContent={<HelpContents />} />
                            }
                            breadCrumbs={[
                                safetyInspectionId 
                                    ?   { 
                                            text: 'Tilbage til sikkerhedsrunderingen...', 
                                            url: `/safetyinspections/${safetyInspectionId}`
                                        }
                                    :   { 
                                            text: 'Tilbage til oversigten...', 
                                            url: `/safetyinspections`
                                        }
                            ]} />

                        <InspectionPointConfiguration inspectionPointUsage={state.inspectionPointUsage} allFacilityTypes={state.facilityTypes} onUpdated={handleUpdate} />
                    </>
                }
            />
        </ProtectedPageWrapper>
    );
}

function HelpContents():JSX.Element
{
    return (
        <section style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <P>
                Når en facilitet tilføjes til en sikkerhedsrundering, afhænger de oprettede kontrolpunkter af facilitetens type. På den måde kan f.eks et køkken og en garderobe have forskellige kontrolpunkter.
            </P>

            <P>
                Hvilke kontrolpunkter der hører til en facilitetstype kan frit ændres, ligesom nye kontrolpunkter kan tilføjes og eksisterende kan fjernes. <b>Bemærk dog at ændringeringerne først træder i kraft når en facilitet tilføjes til en ny sikkerhedsrundering</b>.
                På den måde kan det undgås, at ændre i afviklede sikkerhedsrunderinger, hvilket ville være problematisk i forhold til dokumentation og historik.
            </P>

            <P>
                Det er heller ikke muligt at ændre på titel og instruktion for et kontrolpunkt hvis det allerede er i brug i en sikkerhedsrundering.
            </P>
        </section>
    );
}